import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ErrorBoundary } from '@/components/shared/ErrorBoundary';
import { useAuth } from '@/utils/AuthContext';
import { useLanguage } from '@/i18n/LanguageContext';
import AppLayout from '@/components/layout/AppLayout';
import LoginPage from '@/pages/LoginPage';
import RegisterPage from '@/pages/RegisterPage';
import ForgotPasswordPage from '@/pages/ForgotPasswordPage';
import ResetPasswordPage from '@/pages/ResetPasswordPage';
import EmailVerificationPage from '@/pages/EmailVerificationPage';
import DashboardPage from '@/pages/DashboardPage';
import MarketAnalysisPage from '@/pages/MarketAnalysisPage';
import PersonasPage from '@/pages/PersonasPage';
import PositioningPage from '@/pages/PositioningPage';
import BusinessModelPage from '@/pages/BusinessModelPage';
import MvpPlanPage from '@/pages/MvpPlanPage';
import MarketingStrategyPage from '@/pages/MarketingStrategyPage';
import BuilderListPage from '@/pages/BuilderListPage';
import BuilderEditorPage from '@/pages/BuilderEditorPage';
import FinancialModelPage from '@/pages/FinancialModelPage';
import ResearchPage from '@/pages/ResearchPage';
import ProfilePage from '@/pages/ProfilePage';
import SubscriptionPage from '@/pages/SubscriptionPage';
import ChatPage from '@/pages/ChatPage';
import HelpPage from '@/pages/HelpPage';
import { YandexMetrika } from '@/components/shared/YandexMetrika';

function PrivateRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading } = useAuth();
  const { t } = useTranslation('common');
  const { localizedPath } = useLanguage();

  if (isLoading) {
    return (
      <div className="flex h-screen items-center justify-center">
        <div className="text-lg text-muted-foreground">{t('loading')}</div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to={localizedPath('/login')} replace />;
  }

  return <>{children}</>;
}

function PublicRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading } = useAuth();
  const { t } = useTranslation('common');
  const { localizedPath } = useLanguage();

  if (isLoading) {
    return (
      <div className="flex h-screen items-center justify-center">
        <div className="text-lg text-muted-foreground">{t('loading')}</div>
      </div>
    );
  }

  if (isAuthenticated) {
    return <Navigate to={localizedPath('/dashboard')} replace />;
  }

  return <>{children}</>;
}

/** Shared route tree — used for both /ru (default) and /en prefix */
function appRoutes(dashboardRedirect: string) {
  return (
    <>
      {/* Public routes */}
      <Route path="login" element={<PublicRoute><LoginPage /></PublicRoute>} />
      <Route path="register" element={<PublicRoute><RegisterPage /></PublicRoute>} />
      <Route path="forgot-password" element={<PublicRoute><ForgotPasswordPage /></PublicRoute>} />
      <Route path="reset-password" element={<PublicRoute><ResetPasswordPage /></PublicRoute>} />
      <Route path="verify-email" element={<EmailVerificationPage />} />

      {/* Private routes with shared layout (Sidebar persists) */}
      <Route element={<PrivateRoute><AppLayout /></PrivateRoute>}>
        <Route path="dashboard" element={<DashboardPage />} />
        <Route path="market-analysis" element={<MarketAnalysisPage />} />
        <Route path="personas" element={<PersonasPage />} />
        <Route path="positioning" element={<PositioningPage />} />
        <Route path="business-model" element={<BusinessModelPage />} />
        <Route path="financial-model" element={<FinancialModelPage />} />
        <Route path="mvp-plan" element={<MvpPlanPage />} />
        <Route path="marketing-strategy" element={<MarketingStrategyPage />} />
        <Route path="builder" element={<BuilderListPage />} />
        <Route path="builder/:buildId" element={<BuilderEditorPage />} />
        <Route path="research" element={<ResearchPage />} />
        <Route path="profile" element={<ProfilePage />} />
        <Route path="subscription" element={<SubscriptionPage />} />
        <Route path="chat" element={<ChatPage />} />
        <Route path="help" element={<HelpPage />} />
        <Route path="payment/success" element={<SubscriptionPage />} />
        <Route path="payment/fail" element={<SubscriptionPage />} />
      </Route>

      {/* Default redirect */}
      <Route index element={<Navigate to={dashboardRedirect} replace />} />
      <Route path="*" element={<Navigate to={dashboardRedirect} replace />} />
    </>
  );
}

export default function App() {
  const location = useLocation();

  return (
    <ErrorBoundary key={location.pathname}>
      <YandexMetrika />
      <Routes>
        {/* EN routes with /en prefix */}
        <Route path="/en">
          {appRoutes('/en/dashboard')}
        </Route>

        {/* RU routes (default, no prefix) */}
        <Route path="/">
          {appRoutes('/dashboard')}
        </Route>
      </Routes>
    </ErrorBoundary>
  );
}
